import { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { useSelector } from "react-redux";
import moment from "moment";
import formatNumber, { localeNumber } from "../../hooks/formatNumber";
import { useResizeObserver } from "../widgets/chartWidget/ChartWidget";
import { getFields, findBounds } from "./helpers/graphFields";
import { metricTypes, RANGE_EXTEND, RedArrow, GreenArrow } from "./helpers/constants";
import { useGoalGraphData } from "../../hooks/goalGraphData";

export default function GoalLineAll(props: any) {
	const [initialLoad, setInitialLoad] = useState(true);
	const isLoading = useSelector((state: any) => state.goal.isLoading);
	useEffect(() => {
		if (!isLoading) setInitialLoad(false);
	}, [isLoading]);
	const data = useSelector((state: any) => state.goal.data);
	const graphData = useGoalGraphData(data);
	const [precalcPercent, setPrecalcPercent] = useState(0);
	const metric = metricTypes[data.metricType].metric;
	const fields = getFields(metric, graphData);
	const d3Container = useRef(null);
	const wrapperRef = useRef(null);
	const headerRef = useRef(null);

	const dimensions = useResizeObserver(wrapperRef);
	const headerDimensions = useResizeObserver(headerRef);

	const brandsettings = useSelector((state: any) => state.brandsettings.data);

	const timezone =
		brandsettings && brandsettings.domainSettings && brandsettings.domainSettings.timezone
			? brandsettings.domainSettings.timezone
			: moment.tz.guess();

	useEffect(() => {
		if (!dimensions || !headerDimensions || !graphData || graphData.length === 0) return;
		const currentData = graphData;
		const startDate = new Date(data.iterationStartDate);
		let endDate = null;
		if (props.recurrencyType === "onetime") {
			endDate = new Date(data.endDate);
		} else {
			endDate = new Date(data.iterationEndDate)
		}

		const yOffset = 240;

		const currentTimestamp = new Date(moment().tz(timezone).format("YYYY-MM-DDTHH:mm:ss"));
		const currentPrediction =
			(data.target / (+endDate - +startDate)) * (+currentTimestamp - +startDate);
		setPrecalcPercent(((data.achieved - currentPrediction) / currentPrediction) * 100);

		const [valueRangeStart, valueRangeEnd] = findBounds(currentData, fields);
		const rangeStart = Math.min(valueRangeStart, data.target) * (1 - RANGE_EXTEND / 100);
		const rangeEnd = Math.max(valueRangeEnd, data.target) * (1 + RANGE_EXTEND / 100);

		const svg = d3.select(d3Container.current);
		const xScale = d3
			.scaleTime()
			.range([30, dimensions.width - 10])
			.domain([startDate, endDate]);

		const yScale = d3
			.scaleLinear()
			.range([dimensions.height - headerDimensions.height - yOffset, 0])
			.domain([rangeStart, rangeEnd]);

		const xAxis = d3.axisBottom<Date>(xScale).tickFormat((d) => `${d.toLocaleDateString()}`);
		const yAxis = d3
			.axisRight(yScale)
			.ticks(10)
			.tickFormat((num) => formatNumber(Number(num)));

		svg.select(".x-axis")
			.style("transform", `translateY(${dimensions.height - headerDimensions.height - yOffset}px)`)
			// @ts-ignore
			.call(xAxis);
		// @ts-ignore
		svg.select(".y-axis").style("transform", "translateX(0px)").call(yAxis);

		svg.selectAll(".grid").remove();

		svg.append("g").attr("class", "grid").call(
			d3
				// @ts-ignore
				.axisLeft()
				// @ts-ignore
				.scale(yScale)
				// @ts-ignore
				.tickSize(-dimensions.width, 0, 0)
				.tickFormat(null)
		);

		svg.selectAll(".goal-now").remove();
		svg.selectAll(".goal-target").remove();
		svg.selectAll(".goal-precalc").remove();
		svg.selectAll(".goal-line").remove();

		svg.append("line")
			.attr("class", "goal-now")
			.attr("x1", xScale(new Date(moment().tz(timezone).format("YYYY-MM-DDTHH:mm:ss"))))
			.attr("x2", xScale(new Date(moment().tz(timezone).format("YYYY-MM-DDTHH:mm:ss"))))
			.attr("y1", yScale(rangeStart))
			.attr("y2", yScale(rangeEnd));

		svg.append("line")
			.attr("class", "goal-target")
			.attr("x1", xScale(startDate))
			.attr("x2", xScale(endDate))
			.attr("y1", yScale(data.target))
			.attr("y2", yScale(data.target));

		svg.append("line")
			.attr("class", "goal-precalc")
			.attr("x1", xScale(startDate))
			.attr("x2", xScale(endDate))
			.attr("y1", yScale(0))
			.attr("y2", yScale(data.target));

		const fieldLine = (field: string) =>
			d3
				.line()
				.x((value) => {
					// @ts-ignore
					return xScale(new Date(moment(value.date).tz(timezone).format("YYYY-MM-DDTHH:mm:ss")));
				})
				.y((value) => {
					// @ts-ignore
					return yScale(value[field]);
				})
				.curve(d3.curveCardinal);

		fields.forEach((field) => {
			const line: any = fieldLine(field);
			svg.selectAll("goal-line")
				.data([currentData])
				.join("path")
				.attr("d", line)
				.attr("class", "goal-line")
				.attr("fill", "none")
				.attr("stroke", "white");
		});
	}, [graphData, data, timezone, fields, dimensions, headerDimensions, metric]);

	const graphHTML = initialLoad ? (
		<div>Loading...</div>
	) : (
		<>
			<div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
				<h1>
					{precalcPercent >= 0 ? GreenArrow : RedArrow} {Math.abs(precalcPercent).toFixed(2)}%
				</h1>
			</div>
			<div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1em" }}>
				<div>Target: {data.target.toLocaleString()}</div>
				<ul className="legend" style={{ textAlign: "right" }}>
					<li className="legend-target legend__item">target</li>
					<li className="legend-progress legend__item">progress</li>
				</ul>
			</div>
			<svg className="d3-component" width={"100%"} height={"80%"} ref={d3Container}>
				<g className="x-axis" />
				<g className="y-axis" />
			</svg>
			{!initialLoad && (
				<div
					className="current-progress"
					style={{ textAlign: "center", position: "absolute", bottom: "4rem", width: "100%" }}
				>
					Progress {localeNumber((Number(data.achieved) / Number(data.target)) * 100)}% (
					{data.metricType === 'totalReadingTime' ? (Number(Math.round(data.achieved * 10) / 10).toLocaleString() + ' hrs') : Number(data.achieved).toLocaleString() + ' hits'})
				</div>
			)}
		</>
	);

	return (
		<div className="linechart" ref={wrapperRef}>
			<div className="linechart__heading" ref={headerRef}>
				<div className="linechart__heading-first-row">
					<div style={{ display: "flex", flexDirection: "row", gap: "1rem", cursor: "default" }}>
						<span className="linechart__title">{metricTypes[data.metricType].title} Goal</span>
						{data.recurrencyType !== "daily" && <div onClick={props.onCarouselClick}>○ ●</div>}
					</div>
					{/* {subtitle && <span className="linechart__subtitle">{subtitle}</span>} */}
					<div className="linechart__heading-right">
						<div style={{ flex: 1 }} />
					</div>
				</div>
				{/* <span className="linechart__extra-info">{extraInfo}</span> */}
			</div>
			{graphHTML}
		</div>
	);
}
